// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { XNGAGE_CUSTOM_WIDGET } from "@rpsc/XDL/constants";
import Close from "@rpsc/XDL/Icons/IconClose";
import { fontConfig } from "@rpsc/XDL/PreStyle/typography";
import { color, ellipsis, getSystemColor, layoutContainer, xScreen } from "@rpsc/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC, ReactElement, useState } from "react";
import { css } from "styled-components";

const enum fields {
    backgroundColor = "backgroundColor",
    message = "message",
    dismissible = "dismissible",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
        [fields.message]: string;
        [fields.dismissible]: boolean;
    };
}
type Props = OwnProps;
export interface GlobalWebsiteMessageStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    messageWrapper?: InjectableCss;
    closeClickable?: ClickablePresentationProps;
    closeIcon?: IconProps;
    message?: TypographyPresentationProps;
}
export const globalWebsiteMessageStyles: GlobalWebsiteMessageStyles = {
    wrapper: {
        css: css`
            ${({ backgroundColor }: any) =>
                backgroundColor &&
                css`
                    background: ${backgroundColor};
                `}
        `,
    },
    messageWrapper: {
        css: css`
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 33px;
            ${layoutContainer}
            padding: 0 8px;
        `,
    },
    closeClickable: {
        css: css`
            &:focus {
                outline: none;
            }
            &:hover > ${IconWrapper} {
                color: ${color("common.ui8")};
            }
        `,
    },
    closeIcon: {
        color: "primary",
        src: Close,
        width: 8,
        height: 13,
    },
    message: {
        color: "common.black",
        variant: "p",
        size: 12,
        weight: fontConfig.weight.bold,
        css: css`
            letter-spacing: 0;
            line-height: 14px;
            display: flex;
            justify-content: center;
            width: 100%;
            margin-bottom: 0;
            ${TypographyStyle} {
                font-size: 12px;
                color: ${color("common.black")};
                font-weight: ${fontConfig.weight.bold};
                letter-spacing: 0;
                line-height: 14px;
                max-width: 90%;
                margin: 0;
                ${ellipsis(1)}
                &:empty {
                    display: none;
                }
            }
        `,
    },
};
const styles = globalWebsiteMessageStyles;
const GlobalWebsiteMessage: FC<Props> = ({ fields: { backgroundColor, message, dismissible } }) => {
    const [showGlobalMessage, setShowGlobalMessage] = useState(true);

    const onClose = () => {
        setShowGlobalMessage(false);
    };
    const data = parse(message.replace(/(\n|\<.*\>\<\/.*\>|\<br\>)/gm, ""), parserOptions) as ReactElement[];
    if (!showGlobalMessage || !data || (Array.isArray(data) && data.length === 0)) {
        return null;
    }
    return (
        <StyledWrapper {...styles.wrapper} {...{ backgroundColor }} data-test-selector="global-message-widget">
            <StyledWrapper {...styles.messageWrapper}>
                <Typography {...styles.message} data-test-selector="global_site_message">
                    {data}
                </Typography>
                {dismissible && (
                    <Clickable
                        {...styles.closeClickable}
                        onClick={onClose}
                        data-test-selector="global_site_message_close"
                        aria-label="Global Site Message Close Clickable"
                    >
                        <Icon {...styles.closeIcon} />
                    </Clickable>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};
const widgetModule: WidgetModule = {
    component: GlobalWebsiteMessage,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        displayName: "Global Website Message",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: getSystemColor("info.main"),
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.message,
                displayName: "Global Message Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                placeholder: "Add your message here",
                isRequired: false,
            },
            {
                name: fields.dismissible,
                displayName: "Dismissible?",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
            },
        ],
    },
};
export default widgetModule;
